import {
  ButtonV3,
  GridItem,
  ListItem,
  TypographyV3,
} from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';

import Motion from '../Motion';
import RichText from '../RichText';
import SectionHeading from '../SectionHeading';
import type { FooterCtaProps } from '.';

export type FooterCtaContentProps = Pick<
  FooterCtaProps,
  | 'bulletPoints'
  | 'button'
  | 'description'
  | 'eyebrow'
  | 'indentation'
  | 'inverted'
  | 'multiline'
  | 'title'
  | 'variant'
>;

const FooterCtaContent = ({
  bulletPoints = [],
  button,
  description,
  eyebrow,
  indentation,
  inverted = false,
  multiline = true,
  title,
  variant,
}: FooterCtaContentProps) => {
  const t = useTranslations();
  const { label: buttonLabel, ...buttonProps } = button;

  return (
    <GridItem
      colEnd={{ sm: 'main', md: 23 }}
      colStart={{
        sm: 'main',
        md: variant.lg === 'two_column' ? 15 : 3,
        xl: variant.lg === 'two_column' ? 16 : 3,
      }}
      rowStart={{ sm: variant.sm === 'background' ? 1 : 2, md: 1 }}
    >
      <div
        className={cx('relative flex flex-col py-6 md:px-0', {
          'h-full px-6': variant.sm === 'background',
          'md:h-full md:py-10 lg:py-16': variant.lg === 'background',
          'md:h-auto': variant.lg === 'two_column',
          'text-sandstone-500': inverted,
        })}
        data-cy="footer-cta-content"
      >
        <SectionHeading
          animated
          className={cx({
            'mb-auto': variant.sm === 'background',
            'md:mb-auto': variant.lg === 'background',
          })}
          content={title}
          emAnimationProps={{
            transition: { delay: 0.5, duration: 1, type: 'tween' },
            viewport: { once: true },
          }}
          indentation={cx('pl-14 lg:pl-24', indentation)}
          multiline={multiline}
          typographyProps={{
            className: 'mb-6 leading-tight tracking-tighter md:justify-start',
            color: 'inherit',
            Element: 'h3',
            variant: 'h3',
          }}
          wrapperAnimationProps={{
            initial: { filter: 'blur(5px)', opacity: 0 },
            transition: { delay: 0.2, duration: 0.75, type: 'tween' },
          }}
        />
        {description && (
          <Motion
            className="max-w-96"
            data-cy="footer-cta-description"
            initial={{ opacity: 0 }}
            transition={{ delay: 0.35, duration: 0.5, type: 'tween' }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1 }}
          >
            {eyebrow && (
              <TypographyV3
                color="inherit"
                className="mb-2 uppercase"
                variant={'eyebrow-large'}
              >
                {t(eyebrow)}
              </TypographyV3>
            )}
            <TypographyV3 color="inherit">
              <RichText messageKey={description} />
            </TypographyV3>
            {bulletPoints.map((key, index) => (
              <ListItem
                key={key}
                className={cx('my-2 ml-2 text-inherit', {
                  'mt-4': index === 0,
                })}
              >
                <TypographyV3
                  color="inherit"
                  variant="body-small"
                  className="inline"
                >
                  {t(key)}
                </TypographyV3>
              </ListItem>
            ))}
          </Motion>
        )}
        <Motion
          className="mt-4"
          initial={{ opacity: 0 }}
          transition={{ delay: 0.5, duration: 0.5, type: 'tween' }}
          viewport={{ once: true }}
          whileInView={{ opacity: 1 }}
        >
          <ButtonV3 data-cy="footer-cta-link" {...buttonProps}>
            {t(buttonLabel)}
          </ButtonV3>
        </Motion>
      </div>
    </GridItem>
  );
};

export default FooterCtaContent;
